'use client';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageFromPath } from '@/hooks/useLanguageFromPath';
import { getLocalizedPath } from '@/lib/utils';
import {
  HeaderLogo,
  MenuLink,
  HeaderActions,
  HeaderWrapper,
  MobileMenu,
} from '@/components/layout';

type MainHeaderProps = {
  hideOnScroll?: boolean;
};

const MainHeader = ({ hideOnScroll = false }: MainHeaderProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Handle scroll behavior
  useEffect(() => {
    if (!hideOnScroll) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when scrolling up or at top
      if (currentScrollY < lastScrollY || currentScrollY < 10) {
        setIsVisible(true);
      }
      // Hide header when scrolling down
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hideOnScroll, lastScrollY]);

  const lang = useLanguageFromPath();
  const { t } = useTranslation();

  const startLearningLabel = t('homePage.startLearning');
  const helpLabel = t('homePage.help');
  const libraryLabel = t('homePage.sheets');
  const practiceLabel = t('practicePage.practice');
  const postsLabel = t('postsPage.posts');
  const loginLabel = t('homePage.login', 'Login');
  const manageAccountLabel = t('account.manageAccount', 'Manage account');
  const myProgressLabel = t('account.myProgress', 'My progress');
  const signOutLabel = t('account.sign_out', 'Sign out');

  const homeHref = getLocalizedPath('/', lang);
  const aboutHref = getLocalizedPath('/about', lang);
  const postsHref = lang === 'ru' ? '/posts/ru' : '/posts';
  const practiceHref = getLocalizedPath('/practice', lang) + '?m=PwUybG2uovQ8';

  const headerClassName = hideOnScroll
    ? `transition-transform duration-300 ease-in-out ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`
    : '';

  return (
    <HeaderWrapper className={headerClassName}>
      {/* Logo and Navigation */}
      <div className='flex items-center gap-10'>
        <HeaderLogo href={homeHref} />

        {/* Desktop Navigation */}
        <nav className='hidden lg:flex items-center gap-5 translate-y-[2px]'>
          <MenuLink href={homeHref} label={libraryLabel} />
          <MenuLink href={practiceHref} label={practiceLabel} />
          <MenuLink href={postsHref} label={postsLabel} />
          <MenuLink href={aboutHref} label={helpLabel} />
        </nav>
      </div>

      {/* Desktop Actions - Right */}
      <div className='hidden lg:flex items-center'>
        <HeaderActions
          ctaLabel={startLearningLabel}
          ctaHref={practiceHref}
          loginLabel={loginLabel}
          manageAccountLabel={manageAccountLabel}
          myProgressLabel={myProgressLabel}
          signOutLabel={signOutLabel}
        />
      </div>

      <MobileMenu
        homeHref={homeHref}
        practiceHref={practiceHref}
        postsHref={postsHref}
        aboutHref={aboutHref}
        labels={{
          home: libraryLabel,
          practice: practiceLabel,
          posts: postsLabel,
          about: helpLabel,
          startLearning: startLearningLabel,
          login: loginLabel,
          manageAccount: manageAccountLabel,
          myProgress: myProgressLabel,
          signOut: signOutLabel,
        }}
      />
    </HeaderWrapper>
  );
};

export default MainHeader;
